import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomIn } from 'lucide-react';
import SignedImage from '@/components/ipe/SignedImage';
import { IpeImagePreviewOverlay, useIpeImagePreview } from './IpeImagePreviewOverlay';
import { prefetchStudioDisplayUrls } from '@/pages/ipe/workbench/utils/studioImagePreview';

/**
 * AI 工具工作室 · 当前批次生成结果（主工作区展示，历史记录另查）
 */
export default function AiStudioResultGallery({
  title,
  urls = [],
  onClear,
  clearDisabled = false,
  className = '',
  altPrefix,
  size = 'default',
  enablePreview = true,
}) {
  const { t } = useTranslation('workbench');
  const resolvedTitle = title || t('studioResultGallery.title');
  const resolvedAltPrefix = altPrefix || t('studioResultGallery.altPrefix');
  const {
    previewImage,
    previewScale,
    setPreviewScale,
    previewContainerRef,
    openPreview,
    closePreview,
  } = useIpeImagePreview();

  useEffect(() => {
    if (!urls?.length) return undefined;
    let cancelled = false;
    (async () => {
      await prefetchStudioDisplayUrls(urls);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [urls]);

  if (!Array.isArray(urls) || urls.length === 0) return null;

  const sizeClass = size === 'large' ? ' ai-studio-result-gallery--large' : '';

  return (
    <>
      <div className={`img2img-studio__filled-card ai-studio-result-gallery${sizeClass} ${className}`.trim()}>
        <div className="img2img-studio__filled-head">
          <div>
            <h2 className="img2img-studio__filled-title">{resolvedTitle}</h2>
            <p className="img2img-studio__filled-meta">
              {t('studioResultGallery.count', { count: urls.length })}
              {enablePreview ? t('studioResultGallery.previewHint') : ''}
            </p>
          </div>
          {typeof onClear === 'function' ? (
            <button
              type="button"
              className="img2img-studio__clear-btn"
              onClick={onClear}
              disabled={clearDisabled}
            >
              {t('studioResultGallery.clear')}
            </button>
          ) : null}
        </div>
        <div
          className="img2img-ref-grid img2img-studio__ref-grid ai-studio-result-gallery__grid"
          aria-label={resolvedTitle}
        >
          {urls.map((url, index) => {
            const label = `${resolvedAltPrefix} ${index + 1}`;
            const thumb = (
              <>
                <SignedImage
                  url={url}
                  alt={label}
                  fallback={<span>{t('studioResultGallery.noPreview')}</span>}
                />
                {enablePreview ? (
                  <span className="ai-studio-result-gallery__zoom-badge" aria-hidden="true">
                    <ZoomIn size={16} />
                  </span>
                ) : null}
              </>
            );

            if (!enablePreview) {
              return (
                <div key={`${url}-${index}`} className="img2img-ref-thumb">
                  {thumb}
                </div>
              );
            }

            return (
              <button
                key={`${url}-${index}`}
                type="button"
                className="img2img-ref-thumb ai-studio-result-gallery__thumb is-selectable"
                onClick={() => openPreview(url, label)}
                aria-label={t('studioResultGallery.zoomAria', { label })}
              >
                {thumb}
              </button>
            );
          })}
        </div>
      </div>

      {enablePreview ? (
        <IpeImagePreviewOverlay
          preview={previewImage}
          scale={previewScale}
          setScale={setPreviewScale}
          containerRef={previewContainerRef}
          onClose={closePreview}
        />
      ) : null}
    </>
  );
}
